import React, { useEffect, useState } from 'react';
import { Button, Input, Label, Modal, ModalBody, ModalHeader, Table, UncontrolledTooltip } from 'reactstrap';
import { Trash2 } from 'react-feather';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import {
  BotManagerTask,
  updateBotTask,
  startBotTask,
  startBotTaskRuntime,
} from '../store';

interface BulkDataEntryModalProps {
  isOpen: boolean;
  toggle: () => void;
  task: BotManagerTask | null;
  userType: string;
  userId: string;
  username: string;
  mode?: 'start' | 'update';
  onSuccess?: (dataset?: any[]) => void;
  setActiveTab?: (tab: 'tasks' | 'rpa' | 'assistant' | 'rpa-assistant') => void;
}

const extractDatasetHeaders = (steps: any[] | Record<string, any> = []) => {
  const regex = /\{+([^{}]+?)\}+/g;
  const uniqueKeys = new Set<string>();
  const headers: Array<{ key: string; prompt: string; description: string }> = [];

  const stepsArray = Array.isArray(steps)
    ? steps
    : (steps && typeof steps === 'object'
      ? Object.values(steps)
      : []);

  stepsArray.forEach((step) => {
    const prompt = step?.prompt || step?.description || '';
    const description = step?.Detailed_description || step?.detailed_description || step?.description || step?.prompt || '';
    if (!prompt) return;
    const matches = prompt.matchAll(regex);
    for (const match of matches) {
      const headerKey = match[1]?.trim();
      if (!headerKey || uniqueKeys.has(headerKey)) continue;
      uniqueKeys.add(headerKey);
      headers.push({
        key: headerKey,
        prompt,
        description,
      });
    }
  });

  return headers;
};

const normalizeDatasetRows = (dataset: Record<string, any> | any[] | undefined, headers: Array<{ key: string; prompt: string; description: string }>) => {
  if (Array.isArray(dataset)) {
    if (dataset.length > 0) {
      // If dataset contains object rows, fill any missing headers with empty strings
      return dataset.map((row) => {
        if (!row || typeof row !== 'object') return row;
        const normalizedRow = { ...row };
        headers.forEach((header) => {
          if (normalizedRow[header.key] === undefined) {
            const normH = header.key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
            const matchKey = Object.keys(normalizedRow).find((k) => k.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === normH);
            if (matchKey && normalizedRow[matchKey] !== undefined) {
              normalizedRow[header.key] = normalizedRow[matchKey];
            } else {
              normalizedRow[header.key] = '';
            }
          }
        });
        return normalizedRow;
      });
    }
    return headers.length > 0 ? [Object.fromEntries(headers.map((header) => [header.key, '']))] : [];
  }

  if (dataset && typeof dataset === 'object') {
    const keys = Object.keys(dataset);
    if (keys.length > 0) {
      const firstKey = keys[0];
      const firstVal = (dataset as any)[firstKey];
      if (Array.isArray(firstVal)) {
        const rowCount = firstVal.length;
        const rows = [];
        for (let index = 0; index < rowCount; index += 1) {
          const row: Record<string, any> = {};
          keys.forEach((key) => {
            const values = (dataset as any)[key];
            row[key] = Array.isArray(values) ? values[index] || '' : '';
          });
          rows.push(row);
        }
        return rows;
      } else {
        // Single object dataset dictionary (e.g. { Username: 'fusion64002', Password: '...' })
        const row: Record<string, any> = {};
        headers.forEach((header) => {
          const normHeader = header.key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
          const matchKey = keys.find((k) => k.replace(/[^a-zA-Z0-9]/g, '').toLowerCase() === normHeader) || header.key;
          row[header.key] = (dataset as any)[matchKey] !== undefined ? (dataset as any)[matchKey] : ((dataset as any)[header.key] || '');
        });
        return [row];
      }
    }
  }

  return headers.length > 0 ? [Object.fromEntries(headers.map((header) => [header.key, '']))] : [];
};

const BulkDataEntryModal: React.FC<BulkDataEntryModalProps> = ({
  isOpen,
  toggle,
  task,
  userType,
  userId,
  username,
  mode = 'update',
  onSuccess,
  setActiveTab
}) => {
  const navigate = useNavigate();
  const [headers, setHeaders] = useState<Array<{ key: string; prompt: string; description: string }>>([]);
  const [rows, setRows] = useState<Array<Record<string, any>>>([]);

  useEffect(() => {
    if (isOpen && task) {
      const extractedHeaders = extractDatasetHeaders(task.steps || []);
      const normalizedRows = normalizeDatasetRows(task.Dataset, extractedHeaders);
      setHeaders(extractedHeaders);
      setRows(normalizedRows);

    } else {
      setHeaders([]);
      setRows([]);
    }
  }, [isOpen, task, userType]);



  const handleBulkRemoveRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBulkDatasetChange = (rowIndex: number, key: string, value: string) => {
    setRows((prev) => prev.map((row, i) => (i === rowIndex ? { ...row, [key]: value } : row)));
  };

  const handleConfirmStart = async () => {
    if (!task) return;

    // Close modal immediately when user clicks Start Bot
    toggle();

    try {
      // Save the IPA dataset
      const stepsPayload = task.steps;
      await updateBotTask({
        id: task.id,
        project_title: task.project_title,
        Domain: task.Domain || '',
        Description: task.Description || '',
        ...(stepsPayload !== undefined ? { steps: stepsPayload } : {}),
        dataset: rows,
        credentialsReusable: task.credentialsReusable || 'No',
        UserId: userId,
        Username: username,
        ClientId: task.ClientId || '',
        ClientName: task.ClientName || '',
        templateId: task.templateId || '',
        templateName: task.templateName || '',
        Before: {
          project_title: task.project_title,
          Domain: task.Domain || '',
          Description: task.Description || '',
          ...(stepsPayload !== undefined ? { steps: stepsPayload } : {}),
          dataset: task.Dataset || [],
        },
        After: {
          project_title: task.project_title,
          Domain: task.Domain || '',
          Description: task.Description || '',
          ...(stepsPayload !== undefined ? { steps: stepsPayload } : {}),
          dataset: rows,
          ClientId: task.ClientId || '',
          ClientName: task.ClientName || '',
          templateId: task.templateId || '',
          templateName: task.templateName || '',
        },
      });

      if (mode === 'start') {
        if ((window as any).taskMessagesCache) {
          (window as any).taskMessagesCache.delete(task.id);
        }
        await startBotTask({ userId, username, taskId: task.id, title: task.project_title });
        if (onSuccess) {
          onSuccess(rows);
        }
        if (setActiveTab) {
          setActiveTab('assistant');
        }
        navigate('/bot-manager', { state: { activeTab: 'assistant', focusTaskId: task.id }, replace: true });
        Swal.fire({
          title: 'Success',
          text: 'Bulk data updated successfully and bot started!',
          icon: 'success',
          confirmButtonText: 'OK'
        });
        startBotTaskRuntime({ userId, taskId: task.id, dataset: rows }).catch((runtimeError) => {
          console.warn('IPA Runtime start failed:', runtimeError);
        });
      } else {
        if (onSuccess) {
          onSuccess(rows);
        }
        Swal.fire('Success', 'Bulk data updated successfully!', 'success');
      }
    } catch (error: any) {
      Swal.fire('Error', error.message || 'Failed to submit bulk data', 'error');
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size='xl' centered>
      <ModalHeader toggle={toggle}>Bulk Data Entry - {task?.project_title}</ModalHeader>
      <ModalBody>

        {headers.length > 0 ? (
          <div>

            <div className='table-responsive border rounded' style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table className='mb-0 align-middle' bordered>
                <thead className='table-light'>
                  <tr>
                    {headers.map((header) => (
                      <th
                        key={header.key}
                        style={{
                          minWidth: 180,
                          position: 'sticky',
                          top: 0,
                          zIndex: 5,
                          background: '#f8f9fa',
                          borderTop: 0
                        }}
                      >
                        <div className='fw-semibold text-capitalize text-truncate' title={header.key.replace(/_/g, ' ')}>
                          {header.key.replace(/_/g, ' ')}
                        </div>
                        <div className='small text-primary' style={{ opacity: 0.8, whiteSpace: 'normal', wordBreak: 'break-word' }} title={header.description}>
                          ({header.description})
                        </div>
                      </th>
                    ))}
                    {/* <th
                      style={{
                        width: 70,
                        position: 'sticky',
                        top: 0,
                        right: 0,
                        zIndex: 6,
                        background: '#f8f9fa',
                        borderTop: 0,
                        borderLeft: '1px solid #dee2e6'
                      }}
                    >
                      Action
                    </th> */}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr key={`bulk-row-${rowIndex}`}>
                      {headers.map((header) => (
                        <td key={`${rowIndex}-${header.key}`} className='p-2'>
                          <Input
                            className='form-control-sm'
                            style={{ borderRadius: '4px' }}
                            value={row[header.key] || ''}
                            onChange={(event) => handleBulkDatasetChange(rowIndex, header.key, event.target.value)}
                          />
                        </td>
                      ))}
                      {/* <td
                        className='text-center'
                        style={{
                          position: 'sticky',
                          right: 0,
                          background: '#fff',
                          zIndex: 4,
                          borderLeft: '1px solid #dee2e6'
                        }}
                      >
                        <button id={`remove-row-${rowIndex}`} type='button' className='btn btn-link text-danger p-0' onClick={() => handleBulkRemoveRow(rowIndex)}>
                          <Trash2 size={16} />
                        </button>
                        <UncontrolledTooltip placement="top" target={`remove-row-${rowIndex}`} style={{ backgroundColor: "#355876", color: "white" }}>
                          Remove Row
                        </UncontrolledTooltip>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

          </div>
        ) : (
          <div className='text-center py-4 text-muted'>No input variables found in this bot&apos;s steps.</div>
        )}

        <div className='d-flex justify-content-end gap-2 mt-4'>
          <Button color='light' onClick={toggle}>Close</Button>
          <Button color='primary' onClick={handleConfirmStart}>
            {mode === 'start' ? 'Start Bot' : 'Update Data'}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default BulkDataEntryModal;
